// utils/focusTrap.ts

import { addEventListener, removeEventListener } from './events'; // Assuming you have utilities for adding/removing listeners

const focusTraps: Map<HTMLElement, (e: Event) => void> = new Map();

/**
 * Gets the currently focused element, traversing nested shadow roots
 * to find the deepest active element.
 *
 * @param {Document} root - The document or shadow root to begin searching from.
 * @returns {Element | null} The deepest active element, or null if no element is focused.
 */
function getDeepActiveElement(root: Document | ShadowRoot = document): Element | null {
	let activeElement = root.activeElement;

	while (activeElement?.shadowRoot?.activeElement) {
		activeElement = activeElement.shadowRoot.activeElement;
	}

	return activeElement;
}

/**
 * Applies a focus trap to keep focus within the specified element.
 * Automatically focuses the first focusable element within the element.
 *
 * @param {HTMLElement} element - The element to trap focus within.
 * @param {boolean} [includeNested=false] - If true, includes all focusable elements within the element, including nested ones.
 */
export function applyFocusTrap(element: HTMLElement, includeNested: boolean = false) {
	// Determine the root (either shadow root or document)
	const root = element.shadowRoot || (element.getRootNode() as Document | ShadowRoot);

	// Find focusable elements within the specified element
	const focusableSelectors =
		'button, a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

	// Get all focusable elements
	const allFocusableElements = Array.from(
		element.querySelectorAll<HTMLElement>(focusableSelectors),
	);

	// Conditionally filter elements based on the 'includeNested' parameter
	const focusableElements = includeNested
		? allFocusableElements
		: allFocusableElements.filter(
				(el) => el.closest(element.tagName.toLowerCase()) === element,
			);

	if (focusableElements.length === 0) return; // No focusable elements, exit early

	const firstItem = focusableElements[0];
	const lastItem = focusableElements[focusableElements.length - 1];

	// Automatically focus on the first focusable element
	firstItem.focus();

	// Create a function to handle the keydown event
	const handleKeydown = (e: Event) => {
		const keyboardEvent = e as KeyboardEvent;
		const isTabPressed = keyboardEvent.key === 'Tab' || keyboardEvent.keyCode === 9;
		if (!isTabPressed) return;

		const activeElement = getDeepActiveElement(root);

		if (keyboardEvent.shiftKey) {
			// Shift + Tab
			if (activeElement === firstItem) {
				lastItem.focus();
				keyboardEvent.preventDefault();
			}
		} else {
			// Tab
			if (activeElement === lastItem) {
				firstItem.focus();
				keyboardEvent.preventDefault();
			}
		}
	};

	// Add the keydown listener to the element
	addEventListener(element, 'keydown', handleKeydown);

	// Store the event handler for future removal
	focusTraps.set(element, handleKeydown);
}

/**
 * Clears the focus trap, removing event listeners from the specified element.
 *
 * @param {HTMLElement} element - The element to remove the focus trap from.
 */
export function clearFocusTrap(element: HTMLElement) {
	// Retrieve the event handler and remove it if it exists
	const handler = focusTraps.get(element);
	if (handler) {
		removeEventListener(element, 'keydown', handler);
		focusTraps.delete(element); // Remove the reference to the event handler
	}
}
